import type { ReactNode } from "react";
import {
  Radio as AriaRadio,
  RadioGroup as AriaRadioGroup,
  type RadioGroupProps as AriaRadioGroupProps,
  type RadioProps as AriaRadioProps,
  FieldError,
  Label,
  Text,
} from "react-aria-components";
import { cn } from "@core/utils";

interface RadioGroupProps extends Omit<
  AriaRadioGroupProps,
  "className" | "children"
> {
  /** Accessible label for the whole group. Always required; hide it with `hideLabel`. */
  label: string;
  hideLabel?: boolean;
  description?: string;
  errorMessage?: string;
  children?: ReactNode;
  className?: string;
}

/**
 * Radio group primitive (own-the-code) on React Aria's `RadioGroup`.
 *
 * Exists because a `Select` cannot show WHY you would pick one option over another. The round
 * builder's « Type d'appel » is the case that forced it: pilot versus live is a one-time,
 * freezes-at-open decision, and each choice carries a sentence of consequence — "n'engage pas la
 * fondation" against "comptabilisé dans les résultats". Collapsed into a dropdown, that reasoning
 * is invisible at exactly the moment it matters.
 *
 * Rule of thumb for reaching for this over `Select`: two or three options, all of them worth
 * reading, and a choice the user should make deliberately rather than skim past.
 */
export function RadioGroup({
  label,
  hideLabel = false,
  description,
  errorMessage,
  children,
  className,
  ...props
}: RadioGroupProps) {
  return (
    <AriaRadioGroup
      isInvalid={Boolean(errorMessage) || props.isInvalid}
      className={cn("flex w-full flex-col gap-1.5", className)}
      {...props}
    >
      <Label
        className={cn(
          "text-sm-medium text-(--text-secondary)",
          hideLabel && "sr-only"
        )}
      >
        {label}
        {props.isRequired ? (
          <span aria-hidden className="text-(--text-error)">
            {" "}
            *
          </span>
        ) : null}
      </Label>

      <div className="flex flex-col gap-sm">{children}</div>

      {/* Shown alongside an error, not hidden by one — every other field primitive keeps its
          description visible during an error too, and the guidance is often exactly what explains
          the fix. This one field diverged; brought back in line rather than the other five. */}
      {description ? (
        <Text
          slot="description"
          className="text-sm-regular text-(--text-tertiary)"
        >
          {description}
        </Text>
      ) : null}

      <FieldError className="text-sm-regular text-(--text-error)">
        {errorMessage}
      </FieldError>
    </AriaRadioGroup>
  );
}

interface RadioProps extends Omit<AriaRadioProps, "className" | "children"> {
  /** The choice itself. */
  children: ReactNode;
  /** The consequence of choosing it — the reason this primitive exists over a `Select`. */
  description?: string;
  className?: string;
}

/**
 * One choice inside a {@link RadioGroup}.
 *
 * The dot is a rendered element rather than the native control, so its states come from tokens —
 * the same reason `Checkbox` does it. `relative` is equally load-bearing here: React Aria hides the
 * real `<input>` with `position: absolute`, so without a positioned ancestor the browser scrolls
 * the page to "reveal" it on every click.
 */
export function Radio({
  children,
  description,
  className,
  ...props
}: RadioProps) {
  return (
    <AriaRadio
      className={cn(
        "relative flex cursor-pointer items-start gap-sm outline-none",
        "disabled:cursor-not-allowed",
        className
      )}
      {...props}
    >
      {({ isSelected, isFocusVisible, isDisabled }) => (
        <>
          <span
            className={cn(
              // `mt-0.5` aligns the dot with the first line of the label rather than the centre
              // of a two-line block — the design's `pt-[2px]` on the input column.
              "mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border transition-colors",
              isSelected
                ? "border-(--action-primary-bg) bg-brand-50"
                : "border-(--border-field) bg-surface-card",
              isDisabled && "opacity-50",
              isFocusVisible && "ring-2 ring-focus-ring ring-offset-2"
            )}
          >
            {isSelected ? (
              <span className="size-2 rounded-full bg-action-primary" />
            ) : null}
          </span>

          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-md-medium text-(--text-secondary)">
              {children}
            </span>
            {description ? (
              <span className="text-md-regular text-(--text-tertiary)">
                {description}
              </span>
            ) : null}
          </span>
        </>
      )}
    </AriaRadio>
  );
}
